import { useCallback, useEffect, useRef } from "react";
import { trackPageView } from "@/lib/analytics";
import { Sentry } from "@/lib/sentry";

import { ClerkProvider, SignUp, useClerk, useUser } from '@clerk/react';
import { publishableKeyFromHost } from '@clerk/react/internal';
import { shadcn } from '@clerk/themes';
import { Switch, Route, useLocation, Router as WouterRouter, Redirect } from 'wouter';
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@workspace/api-client-react";
import { Toaster } from "@/components/ui/toaster";

import IssueBoardPage from "@/pages/IssueBoardPage";
import ReportPage from "@/pages/ReportPage";
import MyReportsPage from "@/pages/MyReportsPage";
import StaffDashboardPage from "@/pages/StaffDashboardPage";
import StaffReportPage from "@/pages/StaffReportPage";
import StaffIssuesPage from "@/pages/StaffIssuesPage";
import StaffWhatsChangedPage from "@/pages/StaffWhatsChangedPage";
import WhatsChangedPage from "@/pages/WhatsChangedPage";
import StaffAuditLogPage from "@/pages/StaffAuditLogPage";
import StaffCategoriesPage from "@/pages/StaffCategoriesPage";
import StaffStatusesPage from "@/pages/StaffStatusesPage";
import StaffUsersPage from "@/pages/StaffUsersPage";
import StaffDraftsPage from "@/pages/StaffDraftsPage";
import StaffMcpConnectionsPage from "@/pages/StaffMcpConnectionsPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import { ProtectedStaffRoute, StaffSignIn } from "@/components/staff-auth";

// A 4xx from the API (401/403 session missing, 404, 409) is a final answer,
// so retrying it only delays the error state by several seconds of backoff.
// Network failures and 5xx keep the default three retries.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, err) =>
        !(err instanceof ApiError && err.status < 500) && failureCount < 3,
    },
  },
});

const clerkPubKey = publishableKeyFromHost(
  window.location.hostname,
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
);

const clerkProxyUrl = import.meta.env.VITE_CLERK_PROXY_URL;

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

function stripBase(path: string): string {
  return basePath && path.startsWith(basePath)
    ? path.slice(basePath.length) || "/"
    : path;
}

if (!clerkPubKey) {
  throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY');
}

const clerkLocalization = {
  signIn: { start: { title: "Staff Sign In", subtitle: "Don Norman Associates staff access only" } },
  signUp: { start: { title: "Create Staff Account", subtitle: "Don Norman Associates staff only" } },
};

const clerkAppearance = {
  theme: shadcn,
  cssLayerName: "clerk",
  options: {
    logoPlacement: "inside" as const,
    logoLinkUrl: basePath || "/",
    logoImageUrl: `${window.location.origin}${basePath}/logo.svg`,
  },
  variables: {
    colorPrimary: "hsl(24 95% 50%)",
    colorForeground: "hsl(220 40% 15%)",
    colorMutedForeground: "hsl(215 15% 45%)",
    colorDanger: "hsl(0 84% 60%)",
    colorBackground: "hsl(0 0% 100%)",
    colorInput: "hsl(0 0% 100%)",
    colorInputForeground: "hsl(220 40% 15%)",
    colorNeutral: "hsl(214 20% 88%)",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    borderRadius: "0.5rem",
  },
  elements: {
    rootBox: "w-full flex justify-center",
    cardBox: "bg-white rounded-2xl w-[440px] max-w-full overflow-hidden shadow-lg border border-slate-200",
    card: "!shadow-none !border-0 !bg-transparent !rounded-none",
    footer: "!shadow-none !border-0 !bg-transparent !rounded-none",
    headerTitle: "text-slate-900 font-bold",
    headerSubtitle: "text-slate-500",
    socialButtonsBlockButtonText: "text-slate-700 font-semibold",
    formFieldLabel: "text-slate-800 font-medium",
    footerActionLink: "text-orange-600 hover:text-orange-700 font-semibold",
    footerActionText: "text-slate-500",
    dividerText: "text-slate-400",
    identityPreviewEditButton: "text-orange-600",
    formFieldSuccessText: "text-green-600",
    alertText: "text-red-600",
    logoBox: "mb-6 flex justify-center",
    logoImage: "h-12 w-auto",
    socialButtonsBlockButton: "border border-slate-200 hover:bg-slate-50",
    formButtonPrimary: "bg-orange-500 hover:bg-orange-600 text-white shadow-sm font-semibold rounded-md",
    formFieldInput: "bg-white border-slate-300 focus:ring-orange-500 focus:border-orange-500 rounded-md",
    footerAction: "bg-slate-50 border-t border-slate-100",
    dividerLine: "bg-slate-200",
    alert: "bg-red-50 border border-red-200",
    otpCodeFieldInput: "border-slate-300 focus:ring-orange-500 rounded-md",
    formFieldRow: "mb-4",
    main: "p-6 sm:p-8",
  },
};

function SignInPage() {
  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-slate-50 px-4 py-12 gap-4">
      <StaffSignIn basePath={basePath} />
      <p className="text-xs text-slate-400 text-center max-w-sm">
        Staff access is automatically granted to{" "}
        <span className="font-medium text-slate-500">@donnormanassociates.com</span> accounts.
      </p>
    </div>
  );
}

function SignUpPage() {
  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-slate-50 px-4 py-12 gap-4">
      <SignUp routing="path" path={`${basePath}/sign-up`} signInUrl={`${basePath}/sign-in`} />
      <p className="text-xs text-slate-400 text-center max-w-sm">
        Staff access is automatically granted to{" "}
        <span className="font-medium text-slate-500">@donnormanassociates.com</span> accounts.
      </p>
    </div>
  );
}

function SentryUserSync() {
  const { user } = useUser();
  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.primaryEmailAddress?.emailAddress,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);
  return null;
}

function ClerkQueryClientCacheInvalidator() {
  const { addListener } = useClerk();
  const queryClient = useQueryClient();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const unsubscribe = addListener(({ user }) => {
      const userId = user?.id ?? null;
      if (prevUserIdRef.current !== undefined && prevUserIdRef.current !== userId) {
        queryClient.clear();
      }
      prevUserIdRef.current = userId;
    });
    return unsubscribe;
  }, [addListener, queryClient]);
  return null;
}

function PageViewTracker() {
  const [location] = useLocation();
  useEffect(() => {
    trackPageView(location);
  }, [location]);
  return null;
}

function ClerkProviderWithRoutes() {
  const [, setLocation] = useLocation();
  const routerPush = useCallback((to: string) => setLocation(stripBase(to)), [setLocation]);
  const routerReplace = useCallback((to: string) => setLocation(stripBase(to), { replace: true }), [setLocation]);
  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      proxyUrl={clerkProxyUrl}
      appearance={clerkAppearance}
      signInUrl={`${basePath}/sign-in`}
      signUpUrl={`${basePath}/sign-up`}
      localization={clerkLocalization}
      routerPush={routerPush}
      routerReplace={routerReplace}
    >
      <QueryClientProvider client={queryClient}>
        <ClerkQueryClientCacheInvalidator />
        <SentryUserSync />
        <PageViewTracker />
        <Switch>
          <Route path="/" component={IssueBoardPage} />
          <Route path="/report" component={ReportPage} />
          <Route path="/my-reports" component={MyReportsPage} />
          <Route path="/sign-in/*?" component={SignInPage} />
          <Route path="/sign-up/*?" component={SignUpPage} />
          <Route path="/whats-changed" component={WhatsChangedPage} />
          <Route path="/staff">
            {() => <ProtectedStaffRoute component={StaffDashboardPage} basePath={basePath} />}
          </Route>
          <Route path="/staff/report">
            {() => <ProtectedStaffRoute component={StaffReportPage} basePath={basePath} />}
          </Route>
          <Route path="/staff/issues">
            {() => <ProtectedStaffRoute component={StaffIssuesPage} basePath={basePath} />}
          </Route>
          <Route path="/staff/whats-changed">
            {() => <ProtectedStaffRoute component={StaffWhatsChangedPage} basePath={basePath} />}
          </Route>
          <Route path="/staff/audit-log">
            {() => <ProtectedStaffRoute component={StaffAuditLogPage} basePath={basePath} />}
          </Route>
          <Route path="/staff/categories">
            {() => <ProtectedStaffRoute component={StaffCategoriesPage} basePath={basePath} />}
          </Route>
          <Route path="/staff/statuses">
            {() => <ProtectedStaffRoute component={StaffStatusesPage} basePath={basePath} />}
          </Route>
          <Route path="/staff/mcp-connections">
            {() => <ProtectedStaffRoute component={StaffMcpConnectionsPage} basePath={basePath} />}
          </Route>
          {/* Former path: consent links already handed out by /api/oauth/authorize still land here. */}
          <Route path="/staff/connect-claude">
            {() => <Redirect to={`/staff/mcp-connections${window.location.search}`} replace />}
          </Route>
          <Route path="/staff/drafts">
            {() => <ProtectedStaffRoute component={StaffDraftsPage} basePath={basePath} />}
          </Route>
          <Route path="/staff/users">
            {() => <ProtectedStaffRoute component={StaffUsersPage} basePath={basePath} />}
          </Route>
          <Route path="/terms" component={TermsPage} />
          <Route path="/privacy" component={PrivacyPage} />
        </Switch>
        <Toaster />
      </QueryClientProvider>
    </ClerkProvider>
  );
}

function App() {
  return (
    <WouterRouter base={basePath}>
      <ClerkProviderWithRoutes />
    </WouterRouter>
  );
}

export default App;
